/**
 * AnnouncementsManager — Super Admin UI for sending "What's New" announcements.
 * Lives alongside Email Flows in the super admin panel.
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Send,
  PartyPopper,
  Video,
  Clock,
  X
} from 'lucide-react';
import RichTextEditor from '@/components/shared/RichTextEditor';
import { sanitizeHtml } from '@/utils/sanitize';
import { useDesign } from '@/contexts/DesignContext';
import StudioToolHeader from '@/components/studio/admin/StudioToolHeader';
import { Megaphone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function extractLoomId(url) {
  if (!url) return null;
  const match = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function AnnouncementsManager({ userEmail, onClose }) {
  const { isStudio } = useDesign();
  const [message, setMessage] = useState('');
  const [loomUrl, setLoomUrl] = useState('');
  const [showVideoInput, setShowVideoInput] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/announcements?user_email=${encodeURIComponent(userEmail)}`);
      if (res.ok) {
        const data = await res.json();
        setHistory(data.announcements || []);
      }
    } catch {}
    setLoading(false);
  };

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/announcements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: userEmail,
          message: message.trim(),
          loom_url: loomUrl.trim() || null,
          send_email: sendEmail,
        }),
      });
      if (res.ok) {
        toast.success(sendEmail ? 'Announcement sent + emails queued!' : 'Announcement sent to all church admins!');
        setMessage('');
        setLoomUrl('');
        setShowVideoInput(false);
        setSendEmail(false);
        fetchHistory();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send announcement');
      }
    } catch {
      toast.error('Network error — please try again');
    } finally {
      setSending(false);
    }
  };

  const loomPreviewId = extractLoomId(loomUrl);

  return (
    <div className={isStudio ? 'st31-page' : 'min-h-screen bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-slate-100 dark:to-[#2d2f33] p-4 sm:p-6'}>
      {isStudio && (
        <StudioToolHeader
          icon={Megaphone}
          title="Announcements"
          sub="Send a “What’s new” popup to every church admin and owner."
          onBack={onClose}
        />
      )}
      <div className={isStudio ? 'st31-pg st31-pg-720' : 'max-w-2xl mx-auto'}>
        {/* Header */}
        {!isStudio && (
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={onClose} className="rounded-xl">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <PartyPopper className="w-5 h-5 text-indigo-500" />
              Announcements
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Send "What&apos;s New" popups to all church admins & owners</p>
          </div>
        </div>
        )}

        {/* Compose */}
        {isStudio && (
          /* Studio (§31): one composer card — the editor, the attached video as a
             200-wide 16:9 tile beside its caption, the email switch as a checkbox
             row, and one full-width primary send. Mock frames 21, 22. */
          <div className="st31-card" style={{ marginBottom: 16 }}>
            <div className="st31-ch"><h2 className="st31-ct">New announcement</h2></div>
            <div className="st31-cb">
              <div className="st31-rte-host">
                <RichTextEditor
                  content={message}
                  onChange={setMessage}
                  placeholder="Write your announcement... (e.g., 'We just launched @mentions!')"
                  minHeight="100px"
                />
              </div>
              {showVideoInput ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Input
                    value={loomUrl}
                    onChange={(e) => setLoomUrl(e.target.value)}
                    placeholder="Paste Loom URL (e.g., https://www.loom.com/share/abc123)"
                    className="flex-1 text-sm"
                    data-testid="announcement-loom-input"
                  />
                  <button type="button" className="st-ibtn" style={{ width: 30, height: 30 }} aria-label="Remove the video"
                    onClick={() => { setShowVideoInput(false); setLoomUrl(''); }}>
                    <X />
                  </button>
                </div>
              ) : (
                <button type="button" className="st-lnk st31-quietlnk" onClick={() => setShowVideoInput(true)} data-testid="announcement-add-video-btn">
                  <Video /> Attach a video
                </button>
              )}
              {loomPreviewId && (
                <div className="st31-vidrow">
                  <div className="st31-vid">
                    <iframe
                      src={`https://www.loom.com/embed/${loomPreviewId}?hide_owner=true&hide_share=true&hide_title=true`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      title="Loom preview"
                    />
                  </div>
                  <span>Loom · the popup shows it at 16:9 under the text.</span>
                </div>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }} data-testid="announcement-email-toggle">
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 dark:border-white/15 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                />
                <span>Also send by email to every active admin</span>
              </label>
              <button
                type="button"
                className="st-btn st-pri st-full"
                onClick={handleSend}
                disabled={sending || !message.trim()}
                data-testid="announcement-send-btn"
              >
                {sending ? 'Sending…' : (<><Send />{sendEmail ? 'Send to all churches and email' : 'Send to all churches'}</>)}
              </button>
            </div>
          </div>
        )}

        {!isStudio && (
        <Card className="mb-6 shadow-lg border-0">
          <CardContent className="pt-5 space-y-3">
            <RichTextEditor
              content={message}
              onChange={setMessage}
              placeholder="Write your announcement... (e.g., 'We just launched @mentions!')"
              minHeight="100px"
            />

            {/* Video URL input */}
            {showVideoInput ? (
              <div className="flex items-center gap-2">
                <Input
                  value={loomUrl}
                  onChange={(e) => setLoomUrl(e.target.value)}
                  placeholder="Paste Loom URL (e.g., https://www.loom.com/share/abc123)"
                  className="flex-1 text-sm"
                  data-testid="announcement-loom-input"
                />
                <button
                  onClick={() => { setShowVideoInput(false); setLoomUrl(''); }}
                  className="text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowVideoInput(true)}
                className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 hover:text-indigo-600 transition-colors"
                data-testid="announcement-add-video-btn"
              >
                <Video className="w-3.5 h-3.5" />
                Attach Loom video
              </button>
            )}

            {/* Loom preview */}
            {loomPreviewId && (
              <div className="aspect-video rounded-lg overflow-hidden bg-slate-100 dark:bg-[#3a3d41] border border-slate-200 dark:border-white/10">
                <iframe
                  src={`https://www.loom.com/embed/${loomPreviewId}?hide_owner=true&hide_share=true&hide_title=true`}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  title="Loom preview"
                />
              </div>
            )}

            {/* Email toggle */}
            <label className="flex items-center gap-2.5 py-1 cursor-pointer" data-testid="announcement-email-toggle">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="w-4 h-4 rounded border-slate-300 dark:border-white/15 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500 dark:focus:ring-indigo-400"
              />
              <span className="text-sm text-slate-600 dark:text-slate-300">Also send via email to all active admins</span>
            </label>

            <Button
              onClick={handleSend}
              disabled={sending || !message.trim()}
              className="w-full rounded-xl bg-indigo-600 hover:bg-indigo-700"
              data-testid="announcement-send-btn"
            >
              {sending ? (
                'Sending...'
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {sendEmail ? 'Send to All Churches + Email' : 'Send to All Churches'}
                </>
              )}
            </Button>
          </CardContent>
        </Card>
        )}

        {/* History */}
        {isStudio ? (
          <>
            <div className="st31-lblsec">Past announcements<span>auto-deletes after 90 days</span></div>
            <div className="st31-card">
              {loading ? (
                [0, 1, 2].map((i) => (
                  <div key={i} className="st31-arow">
                    <span className="st31-c st31-skstat">
                      <span className="st31-sk" style={{ width: '72%', height: 14 }} />
                      <span className="st31-sk" style={{ width: '44%', height: 12 }} />
                    </span>
                  </div>
                ))
              ) : history.length === 0 ? (
                <div className="st-empty" style={{ padding: '36px 24px' }}>
                  <span className="st-glyph"><Megaphone /></span>
                  <b>No announcements yet</b>
                  <span>The first one you send shows up here.</span>
                </div>
              ) : history.map((a) => (
                <div key={a.id} className="st31-arow">
                  <span className="st31-c">
                    <span
                      className="st31-x announcement-content"
                      style={{ WebkitLineClamp: 3 }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(a.message) }}
                    />
                    {a.loom_url && (
                      <span className="st31-m">
                        <Video style={{ width: 12, height: 12 }} />
                        <a href={a.loom_url} target="_blank" rel="noopener noreferrer" className="st-lnk">{a.loom_url.replace(/^https?:\/\/(www\.)?/, '')}</a>
                      </span>
                    )}
                    {a.emailed && <span className="st-chip st-tb-xs" style={{ marginTop: 6 }}>Emailed</span>}
                  </span>
                  <span className="st31-cap st31-num" style={{ flex: 'none', alignSelf: 'flex-start' }}>{formatDate(a.created_at)}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Past Announcements</h2>
            <span className="text-[10px] text-slate-400 dark:text-slate-500 italic">Auto-deletes after 90 days</span>
          </div>
          {loading ? (
            <div className="space-y-2 py-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-12 rounded-xl bg-slate-200/60 dark:bg-white/10 animate-pulse" style={{ animationDelay: `${i * 120}ms` }} />
              ))}
            </div>
          ) : history.length === 0 ? (
            <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-8">No announcements yet</p>
          ) : (
            history.map((a) => (
              <Card key={a.id} className="border border-slate-200 dark:border-white/10 shadow-sm">
                <CardContent className="py-3 px-4">
                  <div className="flex items-start justify-between gap-3">
                    <div
                      className="text-sm text-slate-700 dark:text-slate-200 flex-1 announcement-content"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(a.message) }}
                    />
                    <span className="text-[10px] text-slate-400 dark:text-slate-500 flex items-center gap-1 flex-shrink-0 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {formatDate(a.created_at)}
                    </span>
                  </div>
                  {a.loom_url && (
                    <div className="mt-2 text-xs text-indigo-500 flex items-center gap-1">
                      <Video className="w-3 h-3" />
                      <a href={a.loom_url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate">{a.loom_url}</a>
                    </div>
                  )}
                  {a.emailed && (
                    <span className="inline-block mt-1.5 text-[10px] text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-full font-medium">Emailed</span>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
        )}
      </div>
    </div>
  );
}
